import { TextField as MuiTextField } from '@mui/material';

import { Box, Stack, styled } from '@/shared/ui';
import { Button } from '@/shared/ui/lib/Button';
import { CustomTypography } from '@/shared/ui/Typography';

type MetricTone = 'primary' | 'success' | 'warning' | 'violet';
type DataStatusTone = 'ready' | 'partial' | 'unavailable';

export const AnalyticsRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  color: theme.palette.common.black,
  paddingBottom: '24px',
}));

export const FilterRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  flexWrap: 'wrap',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    justifyContent: 'stretch',
  },
}));

export const FilterControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
  gap: '10px',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const PeriodButtons = styled(Stack)(({ theme }) => ({
  flexWrap: 'wrap',

  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    width: '100%',
  },
}));

export const PeriodButton = styled(Button)(({ theme }) => ({
  minWidth: '92px',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',

  [theme.breakpoints.down('sm')]: {
    flex: '1 1 auto',
    minWidth: 0,
    width: '100%',
  },
}));

export const AnalyticsTabs = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  gap: '6px',
  padding: '4px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const AnalyticsTabButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected, theme }) => ({
  minHeight: '36px',
  minWidth: '156px',
  border: 'none',
  borderRadius: '6px',
  backgroundColor: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.common.white : theme.palette.custom.black[70],
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: selected ? 600 : 500,
  lineHeight: 1.2,
  padding: '8px 14px',
  textAlign: 'center',

  '&:hover': {
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.custom.grey7,
  },

  [theme.breakpoints.down('sm')]: {
    flex: '1 1 0',
    minWidth: 0,
  },
}));

export const CustomRange = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const RangeField = styled(MuiTextField)(({ theme }) => ({
  width: '142px',

  '& .MuiInputBase-root': {
    minHeight: '45px',
    borderRadius: '8px',
    backgroundColor: theme.palette.common.white,
  },

  '& .MuiInputBase-input': {
    fontSize: '13px',
  },

  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const RangeErrorState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '24px',
  border: `1px solid ${theme.palette.error.light}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.error.main,
}));

export const DataState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '24px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.custom.black[60],
}));

export const KpiGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '16px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const metricTone: Record<MetricTone, string> = {
  primary: '#0078D4',
  success: '#519C66',
  warning: '#F78400',
  violet: '#838EBE',
};

export const MetricCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: MetricTone }>(({ theme, tone }) => ({
  display: 'flex',
  minHeight: '132px',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: '20px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderTop: `4px solid ${metricTone[tone]}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,
}));

export const MetricHeader = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  color: theme.palette.custom.black[40],
}));

export const MetricValue = styled(CustomTypography)(() => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: '4px',
  fontSize: '34px',
  lineHeight: 1,
}));

export const MetricSuffix = styled('span')(({ theme }) => ({
  color: theme.palette.custom.black[40],
  fontSize: '16px',
  fontWeight: 500,
}));

export const DenseMetricGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: '12px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const CompactMetricCard = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: '8px',
  minHeight: '104px',
  padding: '16px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,
}));

export const CompactMetricLabel = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
  fontSize: '13px',
  lineHeight: 1.3,
}));

export const CompactMetricValue = styled(CustomTypography)(({ theme }) => ({
  overflowWrap: 'anywhere',
  color: theme.palette.custom.black[100],
  fontSize: '26px',
  fontWeight: 700,
  lineHeight: 1.1,
}));

export const CompactMetricHint = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
  fontSize: '12px',
  lineHeight: 1.35,
}));

export const MainGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1.55fr) minmax(320px, 0.85fr)',
  gap: '16px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const BottomGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: '16px',

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const Panel = styled(Box)(({ theme }) => ({
  padding: '22px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,

  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

export const PanelHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  marginBottom: '22px',

  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
}));

export const PanelHeaderMeta = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
  fontSize: '13px',
  lineHeight: 1.35,
}));

export const SectionGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: '16px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ReadinessGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: '10px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const ReadinessItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '10px',
  minWidth: 0,
  padding: '12px',
  border: `1px solid ${theme.palette.custom.grey1}`,
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,

  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
}));

const statusTone: Record<DataStatusTone, { bg: string; color: string }> = {
  ready: { bg: '#EAF6EF', color: '#2F7D46' },
  partial: { bg: '#FFF4E6', color: '#B35A00' },
  unavailable: { bg: '#FDECEC', color: '#B42318' },
};

export const StatusPill = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: DataStatusTone }>(({ tone }) => ({
  display: 'inline-flex',
  flex: '0 0 auto',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '24px',
  borderRadius: '999px',
  backgroundColor: statusTone[tone].bg,
  color: statusTone[tone].color,
  fontSize: '12px',
  fontWeight: 700,
  lineHeight: 1,
  padding: '5px 9px',
  whiteSpace: 'nowrap',
}));

export const InlineMetaList = styled(Box)(() => ({
  display: 'grid',
  gap: '10px',
}));

export const MetaRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(120px, 0.6fr) minmax(0, 1fr)',
  gap: '12px',
  padding: '10px 0',
  borderBottom: `1px solid ${theme.palette.custom.grey7}`,

  '&:last-of-type': {
    borderBottom: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: '4px',
  },
}));

export const MetaLabel = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
  fontSize: '13px',
}));

export const MetaValue = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[100],
  fontSize: '14px',
  overflowWrap: 'anywhere',
}));

export const TableScroller = styled(Box)(() => ({
  overflowX: 'auto',
}));

export const DataTable = styled('table')(({ theme }) => ({
  width: '100%',
  minWidth: '640px',
  borderCollapse: 'collapse',

  '& th': {
    color: theme.palette.custom.black[40],
    fontSize: '12px',
    fontWeight: 600,
    padding: '0 12px 10px 0',
    textAlign: 'left',
    whiteSpace: 'nowrap',
  },

  '& td': {
    borderTop: `1px solid ${theme.palette.custom.grey7}`,
    color: theme.palette.custom.black[100],
    fontSize: '13px',
    padding: '12px 12px 12px 0',
    verticalAlign: 'top',
  },
}));

export const ListGrid = styled(Box)(() => ({
  display: 'grid',
  gap: '10px',
}));

export const ListItem = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: '6px',
  padding: '12px',
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,
}));

export const Legend = styled(Stack)(() => ({
  flexWrap: 'wrap',
}));

export const LegendItem = styled(CustomTypography, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color, theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  color: theme.palette.custom.black[40],
  fontSize: '12px',

  '&::before': {
    display: 'block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    content: '""',
  },
}));

export const WorkloadChart = styled(Box)(() => ({
  display: 'grid',
  gap: '16px',
}));

export const DayRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '42px minmax(0, 1fr) 56px',
  alignItems: 'center',
  gap: '12px',

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '32px minmax(0, 1fr) 46px',
    gap: '8px',
  },
}));

export const DayLabel = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
}));

export const DayValue = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
  textAlign: 'right',
}));

export const Bars = styled(Box)(() => ({
  display: 'grid',
  gap: '6px',
}));

export const BarTrack = styled(Box)(({ theme }) => ({
  height: '12px',
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,
}));

export const InboundBar = styled(Box)(() => ({
  height: '100%',
  borderRadius: '8px',
  backgroundColor: '#0078D4',
}));

export const OutboundBar = styled(Box)(() => ({
  height: '100%',
  borderRadius: '8px',
  backgroundColor: '#519C66',
}));

export const ChannelList = styled(Box)(() => ({
  display: 'grid',
  gap: '18px',
}));

export const ChannelRow = styled(Box)(() => ({
  display: 'grid',
  gap: '8px',
}));

export const ChannelTop = styled(Stack)(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const ChannelName = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}));

export const ChannelDot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  width: '10px',
  height: '10px',
  flex: '0 0 auto',
  borderRadius: '50%',
  backgroundColor: color,
}));

export const ProgressTrack = styled(Box)(({ theme }) => ({
  height: '8px',
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,
}));

export const ProgressBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<{ color: string }>(({ color }) => ({
  height: '100%',
  borderRadius: '8px',
  backgroundColor: color,
}));

export const FunnelList = styled(Box)(() => ({
  display: 'grid',
  gap: '18px',
}));

export const FunnelRow = styled(Box)(() => ({
  display: 'grid',
  gap: '8px',
}));

export const FunnelMeta = styled(Stack)(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const FunnelTrack = styled(Box)(({ theme }) => ({
  height: '14px',
  overflow: 'hidden',
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,
}));

export const FunnelBar = styled(Box)(({ theme }) => ({
  height: '100%',
  borderRadius: '8px',
  backgroundColor: theme.palette.primary.main,
}));

export const RecommendationList = styled(Box)(() => ({
  display: 'grid',
  gap: '12px',
}));

export const RecommendationItem = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '32px minmax(0, 1fr)',
  alignItems: 'center',
  gap: '12px',
  padding: '14px',
  borderRadius: '8px',
  backgroundColor: theme.palette.custom.grey7,
}));

export const RecommendationIndex = styled('span')(({ theme }) => ({
  display: 'flex',
  width: '32px',
  height: '32px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  fontSize: '14px',
  fontWeight: 700,
}));

export const EmptyText = styled(CustomTypography)(({ theme }) => ({
  color: theme.palette.custom.black[40],
}));
